import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { diaSP, horaSP } from '../lib/datas';
import { gradeDoMes, HOJE_SP, mesAtual, nomeDoMes, somarMeses } from '../lib/agenda';
import { Cartao } from './Interface';

export type DiaMarcado = {
  dia: string;
  titulo: string;
  hora: string;
  eventoId: number;
  funcao: string;
  status: string;
};

const DIAS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

/** Mini calendario do "Meu dia": marca os dias em que a pessoa serve. */
export default function CalendarioDoMes({
  compromissos,
}: {
  compromissos: { inicio: string | null; titulo: string; eventoId: number; funcao: string; status: string }[];
}) {
  const [mes, setMes] = useState(mesAtual());

  const marcados = useMemo(() => {
    const mapa = new Map<string, DiaMarcado[]>();
    for (const c of compromissos) {
      if (!c.inicio) continue;
      const dia = diaSP(c.inicio);
      const lista = mapa.get(dia) ?? [];
      lista.push({
        dia,
        titulo: c.titulo,
        hora: horaSP(c.inicio),
        eventoId: c.eventoId,
        funcao: c.funcao,
        status: c.status,
      });
      mapa.set(dia, lista);
    }
    return mapa;
  }, [compromissos]);

  const doMes = [...marcados.entries()]
    .filter(([dia]) => dia.slice(0, 7) === mes)
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <Cartao>
      <div className="mb-3 flex items-center justify-between">
        <button
          onClick={() => setMes(somarMeses(mes, -1))}
          className="rounded-lg px-2 py-1 text-tinta-400 hover:bg-tinta-100"
        >
          &larr;
        </button>
        <h2 className="text-sm font-semibold text-tinta-900">{nomeDoMes(mes)}</h2>
        <button
          onClick={() => setMes(somarMeses(mes, 1))}
          className="rounded-lg px-2 py-1 text-tinta-400 hover:bg-tinta-100"
        >
          &rarr;
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase text-tinta-400">
        {DIAS.map((d, i) => (
          <span key={`${d}-${i}`}>{d}</span>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-1">
        {gradeDoMes(mes).map(({ dia, doMes: desteMes }) => {
          const escalado = marcados.get(dia) ?? [];
          const hoje = dia === HOJE_SP();
          const temEscala = escalado.length > 0;
          const conteudo = (
            <span
              className={`flex h-9 flex-col items-center justify-center rounded-lg text-xs transition ${
                temEscala
                  ? 'bg-tinta-900 font-bold text-white'
                  : hoje
                    ? 'bg-tinta-900 font-bold text-white'
                    : 'text-tinta-600 hover:bg-tinta-100'
              } ${desteMes ? '' : 'opacity-30'}`}
              title={escalado.map((e) => `${e.hora} ${e.titulo} · ${e.funcao}`).join('\n')}
            >
              {Number(dia.slice(8))}
              {escalado.length > 1 && <span className="text-[8px] leading-none">{escalado.length}</span>}
            </span>
          );
          return temEscala ? (
            <Link key={dia} to={`/culto/${escalado[0]!.eventoId}`}>
              {conteudo}
            </Link>
          ) : (
            <span key={dia}>{conteudo}</span>
          );
        })}
      </div>

      {doMes.length > 0 ? (
        <ul className="mt-3 space-y-1 border-t border-tinta-200 pt-3">
          {doMes.slice(0, 4).map(([dia, lista]) => (
            <li key={dia} className="flex items-center gap-2 text-xs">
              <span className="w-8 shrink-0 font-bold text-tinta-900">{Number(dia.slice(8))}</span>
              <span className="min-w-0 flex-1 truncate text-tinta-600">
                {lista[0]!.titulo} · {lista[0]!.funcao}
              </span>
              <span className="shrink-0 text-tinta-400">{lista[0]!.hora}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 border-t border-tinta-200 pt-3 text-xs text-tinta-400">
          Voce nao tem escala neste mes.
        </p>
      )}
    </Cartao>
  );
}
